import { useMemo, useState } from 'react';

import { ComponentDraw } from 'approot/activity-builder/components/component.constants';
import { ErrorMessageActionable } from 'approot/shared/error-message/error-message-actionable';
import Bouncer from 'approot/shared/bouncer/bouncer';
import { ClassAnswerNoStudents } from '../class-answer-empty';
import { ClassroomStudentIdentifierWithVisibility } from '../classroom-student-identifier';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { useGetClassStudents } from '../classroom-student.hooks';
import SubActivityView from 'approot/activity-builder/components/sub-activity/sub-activity-view';
import {
  ClassroomAnswerGrid,
  ClassroomAnswerGridButton,
  ClassroomAnswerGridInner,
  ClassroomAnswerGridItem,
  ClassroomAnswerInstruction,
} from '../classroom-answer.style';
import { getStudentDrawAnswers } from './draw-response-individual.utils';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import ClassroomAwaitingResponse from '../classroom-awaiting';
import ClassroomAnswerDetailHeader from '../classroom-answer-detail-header';
import { useActivitySheetScrollReset } from 'approot/activity-builder/components/shared/sheet/use-activity-sheet-scroll-reset';
import { ActivitySheetInnerMax } from 'approot/activity-builder/components/shared/sheet/activity-sheet.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import ClassroomAnswerStudentVisibility from '../classroom-answer-student-visibility';
import { useGetStudentAnswersOverview } from 'approot/activity-builder/present/use-get-student-answers-overview';
import DrawResponseIndividualPreview from './draw-response-individual-preview';
import { LanguageCodes } from 'global/constants';

type Props = {
  component: Omit<ComponentDraw, '__typename'>;
  language: LanguageCodes;
};

const DrawResponseIndividual = ({ component, language }: Props) => {
  const [activeStudent, setActiveStudent] = useState<number>(0);

  const { userClassId } = useActivityBuilderPresentContext();

  const {
    data: studentsData,
    loading: studentsLoading,
    error: studentsError,
  } = useGetClassStudents({
    classId: Number(userClassId) || 1,
  });

  const { data, error: studentAnswersError } = useGetStudentAnswersOverview({
    fetchPolicy: 'cache-first',
    skip: !userClassId,
    classId: Number(userClassId),
    componentId: component.id,
  });

  const studentsInClass = useMemo(
    () => studentsData?.getClassStudents || [],
    [studentsData]
  );

  const studentAnswers = useMemo(
    () => data?.getClassStudentsAnswers || [],
    [data]
  );

  const results = useMemo(() => {
    const students =
      activeStudent > 0
        ? studentsInClass.filter(s => s.id === activeStudent)
        : studentsInClass;

    return getStudentDrawAnswers({
      studentAnswers,
      studentsInClass: students,
    });
  }, [activeStudent, studentsInClass, studentAnswers]);

  useActivitySheetScrollReset(activeStudent);

  const hasAnswer = useMemo(() => {
    return results.some(r => r.hasAnswer);
  }, [results]);

  if (studentsLoading || !studentAnswers) {
    return <Bouncer />;
  }

  if (studentAnswersError || studentsError) {
    return <ErrorMessageActionable ariaPage="Draw responses" paddingTop={0} />;
  }

  if (studentsInClass.length === 0) {
    return <ClassAnswerNoStudents />;
  }

  return (
    <ClassroomAnswerInstruction>
      {activeStudent > 0 ? (
        <ActivitySheetInnerMax>
          <ClassroomAnswerDetailHeader
            studentId={activeStudent}
            students={studentsInClass}
            onSelect={setActiveStudent}
          />
          <SubActivityView subActivityDetails={component.subActivityProperties}>
            {hasAnswer ? (
              <DrawResponseIndividualPreview
                componentId={component.id}
                classId={Number(userClassId)}
                dimensionsData={component.properties.dimensionData}
                imageUrl={component.properties.image?.original}
                studentName={results[0].studentName}
                uuid={results[0].uuid}
                questionData={component.properties.draw}
                language={language}
              />
            ) : (
              <ClassroomAwaitingResponse />
            )}
          </SubActivityView>
        </ActivitySheetInnerMax>
      ) : (
        <SubActivityView subActivityDetails={component.subActivityProperties}>
          <Box $justifyContent="flex-end" $marginBottom={24}>
            <ClassroomAnswerStudentVisibility />
          </Box>
          <ClassroomAnswerGrid>
            {results.map(result => (
              <ClassroomAnswerGridItem key={result.studentId}>
                <ClassroomAnswerGridInner>
                  {result.hasAnswer ? (
                    <>
                      <ClassroomAnswerGridButton
                        type="button"
                        onClick={() => {
                          setActiveStudent(result.studentId);
                        }}
                      >
                        <ScreenReaderOnly>
                          {`View ${result.studentName}'s response`}
                        </ScreenReaderOnly>
                      </ClassroomAnswerGridButton>
                      <DrawResponseIndividualPreview
                        componentId={component.id}
                        classId={Number(userClassId)}
                        dimensionsData={component.properties.dimensionData}
                        imageUrl={component.properties.image?.original}
                        studentName={result.studentName}
                        uuid={result.uuid}
                        questionData={component.properties.draw}
                        language={language}
                      />
                    </>
                  ) : (
                    <ClassroomAwaitingResponse />
                  )}
                </ClassroomAnswerGridInner>
                <ClassroomStudentIdentifierWithVisibility
                  name={result.studentName}
                  emoji={result.studentEmoji}
                />
              </ClassroomAnswerGridItem>
            ))}
          </ClassroomAnswerGrid>
        </SubActivityView>
      )}
    </ClassroomAnswerInstruction>
  );
};

export default DrawResponseIndividual;
